import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { Attachment, EditableCompensation, Rule } from 'types';
import { REVERSE_DATE_FORMAT } from 'constants-module';
import { Currency } from 'enums';

export const useCompensationForm = (compensation: EditableCompensation | undefined, rules: Rule[]) => {
  const [form] = Form.useForm();
  const type = Form.useWatch('type', form);
  const status = Form.useWatch('status', form);

  const [attachments, setAttachments] = useState<Attachment[]>(compensation?.attachments || []);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);

  const rule = useMemo(() => {
    return rules.find(({ compensationType }) => compensationType === type);
  }, [rules, type]);

  useEffect(() => {
    form.setFieldsValue({
      ...compensation,
      billedOn: compensation?.billedOn ? dayjs(compensation.billedOn) : null,
      targetPeriod: compensation?.targetPeriod ? dayjs(compensation.targetPeriod).format(REVERSE_DATE_FORMAT) : null,
      targetPeriods: compensation?.targetPeriod ? [dayjs(compensation.targetPeriod).format(REVERSE_DATE_FORMAT)] : null,
    });
  }, [form, compensation]);

  useEffect(() => {
    if (rule?.fixedAmount) {
      form.setFieldValue('currency', Currency.USD);
      form.setFieldValue('amount', rule.amount);
    }
  }, [form, rule]);

  useEffect(() => {
    form.setFieldValue('attachmentsCounter', attachments.length + newAttachments.length);
    form.validateFields(['attachmentsCounter']);
  }, [form, attachments, newAttachments]);

  const getUpdatedCompensation = useCallback(
    () => ({
      ...compensation,
      ...form.getFieldsValue(),
      attachments,
      newAttachments,
    }),
    [compensation, form, attachments, newAttachments],
  );

  return {
    form,
    type,
    status,
    rule,
    attachments,
    newAttachments,
    setAttachments,
    setNewAttachments,
    getUpdatedCompensation,
  };
};
